/**
 * Key Derivation Functions for PIN-based encryption.
 *
 * KDF Evaluation (FOUND-23):
 *
 * PBKDF2 (recommended, primary):
 * - Uses @noble/hashes (independently audited by Cure53)
 * - 600,000 iterations of HMAC-SHA256
 * - Universally supported, no WASM dependency
 * - Meets OWASP recommendations (minimum 600,000 for PBKDF2-SHA256)
 *
 * Argon2id (deferred, feature-flagged):
 * - argon2id npm package: ~7KB, SIMD fallback works cross-browser
 * - NOT audited by a major security firm
 * - Superior memory-hard properties (resists GPU/ASIC attacks)
 * - Recommendation: Use PBKDF2 now. Add Argon2id behind a feature flag
 *   after the argon2id package receives a formal audit, OR after we verify
 *   the WASM implementation against reference test vectors ourselves.
 *
 * Benchmark results (captured during implementation):
 * - PBKDF2 600k iterations: ~200-400ms (varies by device)
 * - Argon2id (m=64MB, t=3, p=4): ~300-500ms (varies by device/WASM support)
 *
 * Decision: PBKDF2 is the production KDF. Argon2id is available for
 * evaluation and future feature-flagged use.
 *
 * @module crypto-kdf
 */

import { pbkdf2 } from '@noble/hashes/pbkdf2';
import { sha256 } from '@noble/hashes/sha256';

/**
 * Recommended KDF for production use.
 * PBKDF2 uses @noble/hashes which is independently audited by Cure53.
 */
export const KDF_RECOMMENDATION = 'pbkdf2' as const;

/** Number of PBKDF2 iterations. Meets OWASP 2024 minimum recommendation. */
export const PBKDF2_ITERATIONS = 600_000;

/**
 * Reduced iterations for test environments.
 * Full 600k rounds can timeout on slow CI runners. Tests verify the
 * logic works correctly — the iteration count is a security parameter,
 * not a correctness parameter.
 */
export const PBKDF2_ITERATIONS_TEST = 1_000;

/** Output key length in bytes (256-bit key for AES-256-GCM). */
export const KEY_LENGTH_BYTES = 32;

/**
 * Derive a 256-bit encryption key from a PIN using PBKDF2-SHA256.
 *
 * This is the primary (recommended) KDF for encrypting the master mnemonic
 * in localStorage on web. Uses @noble/hashes which is audited by Cure53.
 *
 * @param pin - User's PIN (typically 4-8 digits)
 * @param salt - Cryptographically random salt (at least 16 bytes)
 * @param iterations - Number of PBKDF2 iterations (default: PBKDF2_ITERATIONS)
 * @returns 32-byte derived key suitable for AES-256-GCM
 */
export function deriveKeyFromPin(
  pin: string,
  salt: Uint8Array,
  iterations: number = PBKDF2_ITERATIONS,
): Uint8Array {
  if (!pin || pin.length === 0) {
    throw new Error('PIN must not be empty');
  }

  if (salt.length < 16) {
    throw new Error('Salt must be at least 16 bytes');
  }

  return pbkdf2(sha256, pin, salt, {
    c: iterations,
    dkLen: KEY_LENGTH_BYTES,
  });
}

/**
 * Derive a 256-bit encryption key from a PIN using Argon2id.
 *
 * WARNING: The argon2id npm package is NOT audited by a major security firm.
 * This function is provided for benchmarking and future feature-flagged use
 * only. Do NOT use in production until the package is formally audited.
 *
 * Parameters chosen per OWASP recommendations:
 * - memory: 64 MB (65536 KiB)
 * - iterations: 3
 * - parallelism: 4
 *
 * @param pin - User's PIN
 * @param salt - Cryptographically random salt (at least 16 bytes)
 * @returns Promise resolving to 32-byte derived key
 */
// Stryker disable all: Argon2id is deferred (requires browser WASM), tested in E2E only
export async function deriveKeyFromPinArgon2id(pin: string, salt: Uint8Array): Promise<Uint8Array> {
  if (!pin || pin.length === 0) {
    throw new Error('PIN must not be empty');
  }

  if (salt.length < 16) {
    throw new Error('Salt must be at least 16 bytes');
  }

  // Dynamic import to avoid loading WASM unless explicitly needed
  const { default: loadWasm } = await import('argon2id');
  const computeHash = await loadWasm();
  const hash = computeHash({
    password: new TextEncoder().encode(pin),
    salt,
    parallelism: 4,
    passes: 3,
    memorySize: 65536,
    tagLength: KEY_LENGTH_BYTES,
  });

  return new Uint8Array(hash);
}
// Stryker restore all
